import type { FabricApp } from "@granular/fabric3-definitions";

/**
 * Collapses the per-sidebar-item app list down to one entry per namespace —
 * one entry per route the Container's Root Router should register.
 *
 * An app may declare several `fabric3.sidebar` items (`apps/data` declares
 * Dashboard and Product Analysis), and each one becomes its own `FabricApp` so
 * it can carry its own flag state, permission model and label into the sidebar.
 * For *routing*, though, they are all the same app: they share a namespace, a
 * lazily-loaded component, and a single `<namespace>/*` path, because the app's
 * own router is what decides which page to render below that path.
 *
 * Registering `<namespace>/*` more than once is what makes this a real problem
 * rather than a tidiness one. React Router serves whichever duplicate it ranked
 * first — the first sidebar item declared — so *that* item's `routable` gates
 * every URL in the namespace. With `app-data-dashboard` off and
 * `app-data-index` on, `/data/index` rendered ComingSoon: Product Analysis was
 * enabled, but the disabled Dashboard item owned the route.
 *
 * So: a namespace is routable if *any* of its sidebar items is, and the item we
 * hand to `GuardedApp` is one that's actually reachable — its permission model
 * is the one worth checking. Which page a user may see *within* the app stays
 * the app's own business; the per-item flags still drive the sidebar, which is
 * where users are steered to a page in the first place.
 */
export const getNamespaceRoutes = (apps: FabricApp[]): FabricApp[] => {
  const byNamespace = new Map<string, FabricApp>();

  for (const app of apps) {
    const claimed = byNamespace.get(app.namespace);

    // First item seen for a namespace takes it; a later one only displaces it
    // by being routable when the incumbent isn't. When nothing is routable the
    // first item stays, and `GuardedApp` renders ComingSoon off it — correct,
    // since the whole namespace is off.
    if (!claimed || (!claimed.routable && app.routable)) {
      byNamespace.set(app.namespace, app);
    }
  }

  return [...byNamespace.values()];
};
